<template>
  <div class="site-setup-other-content">
    <Banner type="secondary">
      <h1>Over to you now, {{firstName}}</h1>
      <h2>Let's piece this puzzle together</h2>
    </Banner>

    <div class="other-content">
      <nav class="star-progress-bar">
        <div class="progress-inner">
          <div class="progress" :style="starWidth"></div>
        </div>
        <div class="star" :class="{'is-active': starActive}">
          <Sprite name="star"/>
        </div>
      </nav>

      <div v-if="isLanding">
        <LazyImage :url="require('../../assets/computer-checklist.svg')" class="mobile-only intro-image"/>
      </div>

      <div v-else>
        <router-view
          v-if="isLoaded"
          :key="_uid"
          class="other-content-view"
          :servicesListId="servicesList.id"
          :servicesPageId="servicesPage.id"
          :reviewsListId="reviewsList.id"
          :reviewsPageId="reviewsPage.id"
          :imageUrl="randomImage"
          @setContinue="setContinue"
        />
      </div>

      <ProBotChat
        :text="continueText"
        :buttonText="continueBtnText"
        :hint="continueHint"
        :link="link"
      />
    </div>
  </div>
</template>

<script>
import {readCompanySitePagesData} from '@/lib/pages'
import {readCompanySitePageListsData} from '@/lib/lists'

import {mapState} from 'vuex'

import Banner from '@/components/Banner'
import LazyImage from '@/components/LazyImage'
import Sprite from '@/components/Sprite'
import ProBotChat from '@/components/SiteSetup/ProBotChat'

export default {
  name: 'otherContent',

  props: {
    companyId: String,
    siteId: String
  },

  data() {
    return {
      totalRequests: 3,
      currentRequest: 0,
      servicesPage: {},
      reviewsPage: {},
      servicesPageLists: [],
      reviewsPageLists: [],
      link: {name: 'logoContent'},
      continueText: `Your home page is looking great! Let's now add some content to your other pages.`,
      continueHint: ''
    }
  },

  components: {
    Banner,
    Sprite,
    ProBotChat,
    LazyImage
  },

  computed: {
    ...mapState(['currentUser']),

    isLoaded() {
      return this.currentRequest === this.totalRequests
    },

    servicesList() {
      return this.servicesPageLists.find((list) => list.name === 'Services')
    },

    firstName() {
      let name = this.currentUser.name || ''
      return name.split(' ')[0]
    },

    reviewsList() {
      return this.reviewsPageLists.find((list) => list.name === 'Reviews')
    },

    randomImage() {
      let pages = []
      pages.push(this.servicesPage)
      pages.push(this.reviewsPage)

      return pages[Math.floor(Math.random() * pages.length)].preview_image
    },

    isLanding() {
      return this.$route.meta.isLanding
    },

    starWidth() {
      let routeName = this.$route.name
      let logoContentRoute = 'logoContent'
      let servicesContentRoute = 'servicesContent'
      let reviewsContentRoute = 'reviewsContent'
      let socialMediaContentRoute = 'socialMediaContent'
      let finalContentRoute = 'otherContentFinal'

      if (routeName === logoContentRoute) return 'width: 20%'
      if (routeName === servicesContentRoute) return 'width: 40%'
      if (routeName === reviewsContentRoute) return 'width: 60%'
      if (routeName === socialMediaContentRoute) return 'width: 80%'
      if (routeName === finalContentRoute) return 'width: 100%'

      return 'width: 0%'
    },

    continueBtnText() {
      let routeName = this.$route.name
      if (routeName === 'otherContentFinal') return 'Preview'
      return 'Continue'
    },

    starActive() {
      let routeName = this.$route.name
      return routeName === 'otherContentFinal'
    }
  },

  methods: {
    getData() {
      this.getSitePages()
    },

    getSitePages() {
      readCompanySitePagesData(this.companyId, this.siteId, {
        page: 1,
        page_size: 10
      }).then(({data, page}) => {
        this.servicesPage = data.find((page) => page.name === 'Services')
        this.reviewsPage = data.find((page) => page.name === 'Reviews')

        this.getPageLists(this.servicesPage)
        this.getPageLists(this.reviewsPage)

        ++this.currentRequest
      })
    },

    getPageLists(page) {
      readCompanySitePageListsData(this.companyId, this.siteId, page.id).then(
        ({data}) => {
          let pageName = page.name.toLowerCase()
          this[pageName + 'PageLists'] = data
          ++this.currentRequest
        }
      )
    },

    setContinue(link, text, hint) {
      this.link = link
      this.continueText = text
      this.continueHint = hint
    }
  },

  watch: {
    '$route'() {
      this.link = {name: 'logoContent'}
    }
  },

  mounted() {
    this.getData()
  }
}
</script>

<style lang="sass">
@import '../../stylesheets/base/variables'
@import '../../stylesheets/base/animations'
@import '../../stylesheets/helpers/mixins'
@import '../../stylesheets/helpers/extends'

$star-size: 50px

.other-content
  display: flex
  flex-direction: column
  padding: $gap-medium

  .intro-image, .video-wrapper
    max-width: 400px
    margin: $gap-large auto 0

    video
      width: 100%

.star-progress-bar
  display: block
  position: relative
  background: #fff
  border-radius: 5px
  top: -50px
  width: 100%
  max-width: 800px
  margin: auto
  height: 30px

  .progress-inner
    position: absolute
    border-radius: 5px
    top: $gap-x-small
    left: $gap-x-small
    width: calc(100% - #{$star-size})
    height: 100%
    overflow: hidden
    background: rgba(0, 0, 0, 0.05)

    .progress
      height: 100%
      transition: width .5s ease-in-out

      @include themify($themes)
        background: themed('color-primary')

  .star
    width: $star-size + 10
    height: $star-size + 10
    background: #fff
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.15)
    display: block
    border-radius: 50%
    position: absolute
    right: -5px
    top: -12px

    .sprite
      width: 100%
      height: 100%
      padding: $gap-small
      fill: rgba(0, 0, 0, 0.15)
      transition: fill .5s .3s ease-in-out

    &.is-active
      animation: pulse 1s infinite ease-in-out

      .sprite
        @include themify($themes)
          fill: themed('color-primary')
</style>
